import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building2, RefreshCw, ShieldAlert, TriangleAlert } from 'lucide-react';

import { useAdminOrganizationDetailQuery } from '@/client/api/admin/organizations';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { OrgStateBadges } from '../OrgStateBadges';

import OverviewTab from './tabs/OverviewTab';
import MembersTab from './tabs/MembersTab';
import InvitesTab from './tabs/InvitesTab';
import ListingsTab from './tabs/ListingsTab';
import AuditTab from './tabs/AuditTab';
import DangerZoneTab from './tabs/DangerZoneTab';

type TabKey =
  | 'overview'
  | 'members'
  | 'invites'
  | 'listings'
  | 'audit'
  | 'danger';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'members', label: 'Members' },
  { key: 'invites', label: 'Invites' },
  { key: 'listings', label: 'Listings' },
  { key: 'audit', label: 'Audit logs' },
  { key: 'danger', label: 'Danger zone' },
];

const OrganizationDetailPage = () => {
  const { oid: oidParam } = useParams<{ oid: string }>();
  // `<oid>` accepts int, ffid, or uuid — pass the path segment through as-is.
  const oid = oidParam?.trim() ? oidParam.trim() : null;

  useBreadcrumbs([
    { label: 'Organizations', href: '/admin/organizations' },
    { label: oid ? `Organization ${oid}` : 'Organization' },
  ]);

  const detailQuery = useAdminOrganizationDetailQuery(oid);
  const org = detailQuery.data;

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const tab: TabKey = TABS.some(t => t.key === tabFromUrl)
    ? (tabFromUrl as TabKey)
    : 'overview';
  const setTab = (next: TabKey) => {
    setSearchParams(
      prev => {
        const p = new URLSearchParams(prev);
        if (next === 'overview') p.delete('tab');
        else p.set('tab', next);
        return p;
      },
      { replace: true }
    );
  };

  if (oid !== null && detailQuery.isLoading) {
    return (
      <div className="min-h-screen space-y-4 bg-gray-50 p-6">
        <Skeleton className="h-12 w-72" />
        <Skeleton className="h-10 w-full max-w-2xl" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (detailQuery.isFetchError) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-2xl rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <TriangleAlert className="mx-auto h-8 w-8 text-red-600" />
          <h1 className="mt-4 text-lg font-semibold text-gray-900">
            Couldn&apos;t load this organization
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {getApiErrorMessage(
              detailQuery.error,
              'The request to load organization details failed.'
            )}
          </p>
          <div className="mt-6 flex justify-center gap-2">
            <Button onClick={() => detailQuery.refetch()} disabled={detailQuery.isFetching}>
              <RefreshCw
                className={`mr-2 h-4 w-4 ${detailQuery.isFetching ? 'animate-spin' : ''}`}
              />
              Try again
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/organizations">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to organizations
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-2xl rounded-lg border border-amber-200 bg-amber-50 p-8 text-center">
          <ShieldAlert className="mx-auto h-8 w-8 text-amber-600" />
          <h1 className="mt-4 text-lg font-semibold text-gray-900">
            Organization not found
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {oid === null ? (
              <>
                <span className="font-medium">{oidParam}</span> is not a valid organization id.
              </>
            ) : (
              <>
                No organization exists with oid <span className="font-medium">{oid}</span>.
                It may have been erased, or the link may be wrong.
              </>
            )}
          </p>
          <Button asChild className="mt-6">
            <Link to="/admin/organizations">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to organizations
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const ownerUid = org.user_account?.uid ?? org.owner_uid ?? org.uid;
  const ownerLabel =
    org.user_account
      ? [org.user_account.first_name, org.user_account.last_name].filter(Boolean).join(' ') ||
        org.user_account.email ||
        `uid ${org.user_account.uid}`
      : org.owner || (ownerUid != null ? `uid ${ownerUid}` : null);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{org.name}</h1>
              <p className="mt-1 text-sm text-gray-500">
                oid {org.oid} · {org.ffid ?? org.uuid}
                {ownerLabel && (
                  <>
                    {' · '}
                    {ownerUid != null ? (
                      <Link
                        to={`/admin/users/${ownerUid}`}
                        className="text-blue-600 hover:underline"
                      >
                        Owner: {ownerLabel}
                      </Link>
                    ) : (
                      <span>Owner: {ownerLabel}</span>
                    )}
                  </>
                )}
                {ownerUid == null && !ownerLabel && (
                  <span className="text-amber-700"> · No owner</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <OrgStateBadges org={org} />
            <Button variant="outline" asChild>
              <Link to="/admin/organizations">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Link>
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto border-b border-gray-200">
          <nav className="flex min-w-max gap-6" role="tablist">
            {TABS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={`-mb-px whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                  tab === key
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>

        {tab === 'overview' && <OverviewTab oid={org.oid} org={org} />}
        {tab === 'members' && <MembersTab oid={org.oid} />}
        {tab === 'invites' && <InvitesTab oid={org.oid} />}
        {tab === 'listings' && <ListingsTab oid={org.oid} />}
        {tab === 'audit' && <AuditTab oid={org.oid} />}
        {tab === 'danger' && <DangerZoneTab org={org} />}
      </div>
    </div>
  );
};

export default OrganizationDetailPage;
